import { Injectable } from '@angular/core'
import { environment } from '../../../environments/environment'
import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import {
    EMPTY,
    map,
    MonoTypeOperatorFunction,
    Observable,
    throwError,
    of,
    switchMap,
    take,
    from,
} from 'rxjs'
import { catchError } from 'rxjs/operators'
import { CaseId, MarkerDimension, SegObjectPropertyMap } from '../../state/case/case.state'
import { DimensionDataWithDomain } from '../../state/plot-data/plot-data.state'
import {
    ViewerConfigApiRequest,
    ViewerConfigApiResponse,
    ViewerState,
} from '../../state/viewer/viewer.state'
import {
    FreeSelection,
    FreeSelectionsForCase,
    HierarchicalSelectionNode,
    HierarchicalSelectionRoot,
    HierarchicalSelectionsForCase,
    SelectionType,
} from '../../state/selection/selection.state'
import { mapViewerStateToApiRequest } from '../../state/viewer/viewer-state-utils'
import { CaseUtilsService } from './case-utils.service'
import { Store } from '@ngrx/store'
import { concatLatestFrom } from '@ngrx/effects'
import { selectCaseId } from '../../state/case/case.selectors'
import { selectViewerState } from '../../state/viewer/viewer.selectors'

@Injectable({
    providedIn: 'root',
})
export class DatasetApiService {
    private readonly baseUrl = environment.apiUrl
    private readonly publicUrl = environment.publicApiUrl

    constructor(
        private readonly http: HttpClient,
        private readonly caseUtils: CaseUtilsService,
        private readonly store: Store
    ) {}

    getSegObjectPropertyMaps(caseId: CaseId): Observable<SegObjectPropertyMap[]> {
        return caseId
            ? this.http.get<SegObjectPropertyMap[]>(
                  `${this.baseUrl}/datasets/${caseId}/segObjectPropertyMaps`
              )
            : EMPTY
    }

    getSegObjectPropertiesData(
        caseId: CaseId,
        segObjectPropertyMap: SegObjectPropertyMap
    ): Observable<MarkerDimension[]> {
        return segObjectPropertyMap
            ? this.http
                  .get<MarkerDimension[]>(
                      `${this.baseUrl}/datasets/${caseId}/segObjectPropertyMaps/${segObjectPropertyMap.name}`
                  )
                  .pipe(
                      map(markerDimensions =>
                          markerDimensions.map(marker => ({
                              ...marker,
                              segobjectpropertymap: segObjectPropertyMap, // Assign the segObjectPropertyMap to the new field
                          }))
                      )
                  )
            : EMPTY
    }

    getSegObjectPropertyMapJobSettings(
        caseId: CaseId,
        segObjectPropertyMap: SegObjectPropertyMap
    ): Observable<any> {
        const url = `${this.baseUrl}/datasets/${caseId}/segObjectPropertyMaps/${segObjectPropertyMap.name}/job_settings.json`
        return this.http.get<any>(url).pipe(catchError(() => EMPTY))
    }

    getMarkersForCase(caseId: CaseId): Observable<MarkerDimension[]> {
        return caseId
            ? this.http.get<MarkerDimension[]>(`${this.baseUrl}/datasets/${caseId}/markers`)
            : EMPTY
    }

    getViewerConfigForCase(caseId: CaseId, shared: boolean): Observable<ViewerConfigApiResponse> {
        const baseUrl = shared ? this.publicUrl : this.baseUrl
        return this.http.get<ViewerConfigApiResponse>(`${baseUrl}/datasets/${caseId}`)
    }

    getDataForDimension(
        marker: MarkerDimension,
        caseId: CaseId
    ): Observable<DimensionDataWithDomain> {
        return this.caseUtils.createObservableWithMarkerDimensions<DimensionDataWithDomain>(
            markerDimensions =>
                this.http
                    .get(
                        `${this.baseUrl}/datasets/${caseId}/segObjectPropertyMaps/${
                            marker.segobjectpropertymap.name
                        }/${marker.name.concat('.bin')}`,
                        { responseType: 'arraybuffer' }
                        // `assets/test-data/${caseId}/markers/${marker.fileName}`
                        //  for testing with local data: (marker-data-csv-to-json-and-point-annotations.py)
                    )
                    .pipe(
                        switchMap(binData => {
                            return this.http
                                .get<any>(
                                    `${this.baseUrl}/datasets/${caseId}/segObjectPropertyMaps/${
                                        marker.segobjectpropertymap.name
                                    }/${marker.name.concat('.json')}`
                                )
                                .pipe(map(jsonData => ({ binData, jsonData })))
                        }),
                        map(({ binData, jsonData }) => {
                            const dataView = new DataView(binData)
                            // Each marker point is 2*4=8 bytes (id (4 bytes, uint32) and value (4 bytes, float32))
                            const bytesPerPoint = 8
                            const pointCount = binData.byteLength / bytesPerPoint
                            const points = []
                            // Iterate through the binary data and extract the points.
                            for (let i = 0; i < pointCount; i++) {
                                const id = dataView.getUint32(i * bytesPerPoint, true)
                                const value = dataView.getFloat32(i * bytesPerPoint + 4, true)
                                points.push([id, value])
                            }
                            const markerDimension = markerDimensions.find(
                                markerDimension => markerDimension.name === marker.name
                            )
                            if (!markerDimension) {
                                throw new Error('Could not determine marker boundaries.')
                            }
                            return {
                                domain: {
                                    lowerBound: markerDimension.lowerBound,
                                    upperBound: markerDimension.upperBound,
                                },
                                points: points,
                                legend: jsonData.legend,
                            } as DimensionDataWithDomain
                        }),
                        catchError(error => {
                            console.error('Error fetching marker data:', error)
                            throw error
                        })
                    )
        )
    }

    getFreeSelectionsForCase(caseId: CaseId): Observable<FreeSelectionsForCase> {
        return this.http
            .get<{ [name: string]: SelectionJson }>(`${this.baseUrl}/datasets/${caseId}/selections`)
            .pipe(
                map((selectionsJson: SelectionsJson) => {
                    const selections = new Map<string, FreeSelection>()
                    for (const [name, selectionJson] of Object.entries(selectionsJson)) {
                        selections.set(name, parseSelectionJsonToFreeSelection(selectionJson))
                    }
                    return selections
                })
            )
    }

    getHierarchicalSelectionListForCase(caseId: CaseId): Observable<string[]> {
        return this.http.get<string[]>(`${this.baseUrl}/datasets/${caseId}/hierarchicalSelections`)
    }

    getSingleHierarchicalSelectionForCase(
        caseId: CaseId,
        selectionName: string
    ): Observable<HierarchicalSelectionsForCase> {
        return this.http
            .get<SelectionJson>(
                `${this.baseUrl}/datasets/${caseId}/hierarchicalSelections/${selectionName}`
            )
            .pipe(
                map((selectionsJson: SelectionJson): HierarchicalSelectionsForCase => {
                    const hierarchicalSelectionsForCase = new Map<
                        string,
                        HierarchicalSelectionRoot
                    >()
                    hierarchicalSelectionsForCase.set(
                        selectionName,
                        parseSelectionJsonToHierarchicalSelectionRoot(selectionsJson)
                    )
                    return hierarchicalSelectionsForCase
                })
            )
    }

    getUnsupervisedSelectionListForCase(caseId: CaseId): Observable<string[]> {
        return this.http.get<string[]>(`${this.baseUrl}/datasets/${caseId}/unsupervisedSelections`)
    }

    getSingleUnsupervisedSelectionForCase(
        caseId: CaseId,
        selectionName: string
    ): Observable<HierarchicalSelectionsForCase> {
        return this.http
            .get<SelectionJson>(
                `${this.baseUrl}/datasets/${caseId}/unsupervisedSelections/${selectionName}`
            )
            .pipe(
                map((selectionsJson: SelectionJson): HierarchicalSelectionsForCase => {
                    const unsupervisedSelectionsForCase = new Map<
                        string,
                        HierarchicalSelectionRoot
                    >()
                    unsupervisedSelectionsForCase.set(
                        selectionName,
                        parseSelectionJsonToUnsupervisedSelectionRoot(selectionsJson)
                    )
                    return unsupervisedSelectionsForCase
                })
            )
    }

    saveFreeSelection(caseId: CaseId, selection: FreeSelection): Observable<unknown> {
        if (!selection.name) {
            throw new Error('Error saving selection, invalid selection name.')
        }
        const body: SelectionJson = parseFreeSelectionToSelectionJson(selection)
        return this.http.put<unknown>(
            `${this.baseUrl}/datasets/${caseId}/selections/${encodeURIComponent(selection.name)}`,
            body
        )
    }

    saveHierarchicalSelection(
        caseId: CaseId,
        selection: HierarchicalSelectionRoot
    ): Observable<unknown> {
        if (!selection.name) {
            throw new Error('Error saving selection, invalid selection name.')
        }
        const body: SelectionJson = parseHierarchicalSelectionRootToSelectionJson(selection)

        return this.http.put<unknown>(
            `${this.baseUrl}/datasets/${caseId}/hierarchicalSelections/${encodeURIComponent(
                selection.name
            )}`,
            body
        )
    }

    saveUnsupervisedSelection(
        caseId: CaseId,
        selection: HierarchicalSelectionRoot
    ): Observable<unknown> {
        if (!selection.name) {
            throw new Error('Error saving selection, invalid selection name.')
        }
        const body: SelectionJson = parseHierarchicalSelectionRootToSelectionJson(selection)

        return this.http.put<unknown>(
            `${this.baseUrl}/datasets/${caseId}/unsupervisedSelections`,
            body
        )
    }

    deleteFreeSelection(caseId: CaseId, selectionName: string): Observable<unknown> {
        return this.http.delete<unknown>(
            `${this.baseUrl}/datasets/${caseId}/selections/${selectionName}`
        )
    }

    deleteHierarchicalSelection(caseId: CaseId, selectionName: string): Observable<unknown> {
        return this.http.delete<unknown>(
            `${this.baseUrl}/datasets/${caseId}/hierarchicalSelections/${selectionName}`
        )
    }

    deleteUnsupervisedSelection(caseId: CaseId, selectionName: string): Observable<unknown> {
        return this.http.delete<unknown>(
            `${this.baseUrl}/datasets/${caseId}/unsupervisedSelections/${selectionName}`
        )
    }

    getSelectionsBlobForCase(caseId: CaseId): Observable<Blob> {
        return this.http.get(`${this.baseUrl}/datasets/${caseId}/selectionsDownload`, {
            responseType: 'blob',
        })
    }

    getHierarchicalSelectionsBlobForCase(caseId: CaseId, name: string): Observable<Blob> {
        return this.http.get(
            `${this.baseUrl}/datasets/${caseId}/hierarchicalSelectionsDownload/${name}`,
            {
                responseType: 'blob',
            }
        )
    }

    getUnsupervisedSelectionsBlobForCase(caseId: CaseId, name: string): Observable<Blob> {
        return this.http.get(
            `${this.baseUrl}/datasets/${caseId}/unsupervisedSelectionsDownload/${name}`,
            {
                responseType: 'blob',
            }
        )
    }

    getMarkersBlobForCase(caseId: CaseId, mapName: string): Observable<Blob> {
        const url = `${this.baseUrl}/datasets/${caseId}/download/${mapName}`
        return from(fetch(url).then(response => response.json())).pipe(
            switchMap(url => this.http.get(url, { responseType: 'blob' }))
        )
    }

    getRecurrentNeighborhoodsResults(caseId: CaseId): Observable<any> {
        return this.http.get(`${this.baseUrl}/datasets/${caseId}/rcn`)
    }

    getRecurrentNeighborhoodsResultBlob(url: string): Observable<Blob> {
        return this.http.get(url, {
            responseType: 'blob',
        })
    }

    getNeighborhoodEnrichmentResults(caseId: CaseId): Observable<any> {
        return this.http.get(`${this.baseUrl}/datasets/${caseId}/nhoodEnrichment`)
    }

    getNeighborhoodEnrichmentResultBlob(url: string): Observable<Blob> {
        return this.http.get(url, {
            responseType: 'blob',
        })
    }

    getTsneResults(caseId: CaseId): Observable<any> {
        return this.http.get(`${this.baseUrl}/datasets/${caseId}/tsne`)
    }

    getUmapResults(caseId: CaseId): Observable<any> {
        return this.http.get(`${this.baseUrl}/datasets/${caseId}/umap`)
    }

    getAutoCellDetectionResults(caseId: CaseId): Observable<any> {
        return this.http.get(`${this.baseUrl}/datasets/${caseId}/acd`)
    }

    getAutoCellDetectionResultBlob(url: string): Observable<Blob> {
        return this.http.get(url, {
            responseType: 'blob',
        })
    }

    getPlottingResults(caseId: CaseId): Observable<any> {
        return this.http.get(`${this.baseUrl}/datasets/${caseId}/plotting`)
    }

    deletePlottingResult(caseId: CaseId, name: string): Observable<any> {
        return this.http.delete(`${this.baseUrl}/datasets/${caseId}/plotting/${name}`)
    }

    getSegmentColors(colorSeed: number, segmentIds: number[]): Observable<string[]> {
        return this.http.put<string[]>(`${this.baseUrl}/segmentColors`, {
            colorSeed,
            segmentIds,
        })
    }

    getMarkerMapping(caseId: CaseId): Observable<any> {
        return this.http.get(`${this.baseUrl}/datasets/${caseId}/markers/job_settings.json`).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.status === 404) {
                    return of({}) // Return an empty object if 404 error
                }
                return throwError(() => error) // Re-throw the error for other statuses
            })
        )
    }

    getSnapshotLink(
        caseId: CaseId,
        width: number,
        height: number,
        points: number[]
    ): Observable<{ url: string }> {
        return this.store.select(selectViewerState).pipe(
            take(1),
            switchMap(viewerState => {
                const body = {
                    currentViewstate: mapViewerStateToApiRequest(viewerState),
                    screenSize: [width, height],
                    points: points,
                }
                return this.http.post<{ url: string }>(
                    `${this.baseUrl}/datasets/${caseId}/snapshot`,
                    body
                )
            })
        )
    }

    getSnapshotBlob(url: string): Observable<Blob> {
        return this.http.get(url, {
            responseType: 'blob',
        })
    }

    saveViewerState<T>(): MonoTypeOperatorFunction<T> {
        return (source: Observable<T>): Observable<T> =>
            source.pipe(
                concatLatestFrom(() => this.store.select(selectCaseId)),
                concatLatestFrom(() => this.store.select(selectViewerState)),
                switchMap(([[originalValue, caseId], viewerState]) => {
                    if (caseId === null) return of(originalValue)
                    if (!isValidViewerState(viewerState)) return of(originalValue)
                    return this.postViewerState(
                        mapViewerStateToApiRequest(viewerState),
                        caseId
                    ).pipe(map(() => originalValue))
                })
            )
    }

    private postViewerState(config: ViewerConfigApiRequest, caseId: CaseId): Observable<unknown> {
        return this.http.post<unknown>(`${this.baseUrl}/datasets/${caseId}/state`, config)
    }

    getDatasetSharingLink(): Observable<{ url: string }> {
        return this.store.select(selectCaseId).pipe(
            take(1),
            this.saveViewerState(),
            switchMap(caseId =>
                this.http.post<{ url: string }>(`${this.baseUrl}/datasets/${caseId}/share`, {})
            )
        )
    }
}

function isValidViewerState(viewerState: ViewerState): boolean {
    if (viewerState.position === undefined) {
        console.error('bug: ignoring attempt to store undefined viewer state')
        return false
    }
    if (viewerState.crossSectionScale === 0) {
        // zoom 0 is guarded against in the store (viewerState), should never happen
        console.error('bug: ignoring attempt to store zoom 0 viewer state')
        return false
    }
    return true
}

function parseSelectionJsonToFreeSelection(selectionJson: SelectionJson): FreeSelection {
    return {
        name: selectionJson.name,
        type: SelectionType.Free,
        points: new Set(selectionJson.points),
        segSource: selectionJson.segSource,
        annotationVisible: selectionJson.annotationVisible,
        annotationColor: selectionJson.annotationColor,
        annotationOpacity: selectionJson.annotationOpacity,
        modified: false,
    }
}

function parseSelectionJsonToHierarchicalSelectionRoot(
    selectionJson: SelectionJson
): HierarchicalSelectionRoot {
    return {
        name: selectionJson.name,
        type: SelectionType.Hierarchical,
        children: selectionJson.children?.map(child =>
            parseSelectionJsonToHierarchicalSelectionNode(child)
        ),
        modified: false,
        segSource: selectionJson.segSource,
    }
}

function parseSelectionJsonToHierarchicalSelectionNode(
    selectionJson: SelectionJson
): HierarchicalSelectionNode {
    return {
        name: selectionJson.name,
        points: new Set(selectionJson.points),
        segSource: selectionJson.segSource,
        annotationVisible: selectionJson.annotationVisible,
        annotationColor: selectionJson.annotationColor,
        annotationOpacity: selectionJson.annotationOpacity,
        children: selectionJson.children?.map(child =>
            parseSelectionJsonToHierarchicalSelectionNode(child)
        ),
        modified: false,
    }
}

function parseSelectionJsonToUnsupervisedSelectionRoot(selectionJson: SelectionJson): any {
    return {
        name: selectionJson.name,
        type: SelectionType.Unsupervised,
        children: selectionJson.children?.map(child =>
            parseSelectionJsonToUnsupervisedSelectionNode(child)
        ),
        modified: false,
        segSource: selectionJson.segSource,
    }
}

function parseSelectionJsonToUnsupervisedSelectionNode(
    selectionJson: SelectionJson
): HierarchicalSelectionNode {
    return {
        name: selectionJson.name,
        points: new Set(selectionJson.points),
        segSource: selectionJson.segSource,
        annotationVisible: selectionJson.annotationVisible,
        annotationColor: selectionJson.annotationColor,
        annotationOpacity: selectionJson.annotationOpacity,
        children: selectionJson.children?.map(child =>
            parseSelectionJsonToUnsupervisedSelectionNode(child)
        ),
        modified: false,
    }
}

function parseFreeSelectionToSelectionJson(selection: FreeSelection): SelectionJson {
    return {
        name: selection.name,
        points: Array.from(selection.points),
        segSource: selection.segSource,
        annotationVisible: selection.annotationVisible,
        annotationColor: selection.annotationColor,
        annotationOpacity: selection.annotationOpacity,
        children: undefined,
    }
}

function parseHierarchicalSelectionRootToSelectionJson(
    selection: HierarchicalSelectionRoot
): SelectionJson {
    return {
        name: selection.name,
        points: [],
        segSource: selection.segSource,
        annotationVisible: false,
        annotationColor: '#ffffff',
        annotationOpacity: 1,
        children: selection.children?.map(child =>
            parseHierarchicalSelectionNodeToSelectionJson({
                ...child,
                segSource: selection.segSource,
            })
        ),
    }
}

function parseHierarchicalSelectionNodeToSelectionJson(
    selection: HierarchicalSelectionNode
): SelectionJson {
    return {
        name: selection.name,
        points: Array.from(selection.points),
        segSource: selection.segSource,
        annotationVisible: selection.annotationVisible,
        annotationColor: selection.annotationColor,
        annotationOpacity: selection.annotationOpacity,
        children: selection.children?.map(child =>
            parseHierarchicalSelectionNodeToSelectionJson({
                ...child,
                segSource: selection.segSource,
            })
        ),
    }
}

interface SelectionJson {
    name: string | undefined
    points: number[] // cannot use a Set (serializes to {})
    segSource: string | undefined
    annotationVisible: boolean
    annotationColor: string
    annotationOpacity: number
    children: SelectionJson[] | undefined
}

interface SelectionsJson {
    [name: string]: SelectionJson
}
